import { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonIcon from '@mui/icons-material/Person'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ReactMarkdown from 'react-markdown'
import { API_BASE_URL } from '../../config/env'
import { reportApiError } from '../../lib/reportApiError'

const API_URL = `${API_BASE_URL}/chat`

const SUGGESTED_PROMPTS = [
  'How have agronomy input costs changed over the last five years?',
  'Who currently funds South African farmers, at what rates and for what?',
  'What are the nutritional benefits of regenerative food?',
  'Does South African policy support regenerative agriculture?',
  'What are the costs to transition from conventional to regenerative agriculture?',
  'What is status of the organic agriculture sector in South Africa?',
  'What exactly are the ecological benefits from converting 1HA of land?',
]

interface Source {
  id: string
  title: string
}

interface Message {
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
  error?: boolean
}

function generateSessionId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)
}

export default function ChatAssistant() {
  const sessionId = useRef<string>(generateSessionId())
  const threadRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const [streamingSources, setStreamingSources] = useState<Source[] | null>(null)
  const streamingRef = useRef<{ target: string; index: number; interval: ReturnType<typeof setInterval> | null }>({
    target: '',
    index: 0,
    interval: null,
  })
  const sourceTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)

  const isBusy = loading || streamingText !== null || streamingSources !== null

  const stop = () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    if (streamingRef.current.interval) {
      clearInterval(streamingRef.current.interval)
      streamingRef.current.interval = null
      // Freeze the last assistant message at exactly where rendering stopped
      const partial = streamingRef.current.target.slice(0, streamingRef.current.index)
      if (partial) {
        setMessages((prev) => {
          const updated = [...prev]
          const last = updated[updated.length - 1]
          if (last?.role === 'assistant') {
            updated[updated.length - 1] = { ...last, content: partial, sources: [] }
          }
          return updated
        })
      }
    }
    sourceTimersRef.current.forEach(clearTimeout)
    sourceTimersRef.current = []
    setStreamingText(null)
    setStreamingSources(null)
    setLoading(false)
  }

  const scrollToBottom = () => {
    setTimeout(() => {
      if (threadRef.current) {
        threadRef.current.scrollTop = threadRef.current.scrollHeight
      }
    }, 50)
  }

  const send = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || loading) return

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }])
    setInput('')
    setLoading(true)
    scrollToBottom()

    let res: Response | undefined
    try {
      // Send full conversation history so the model maintains thread context.
      const history = messages.map((m) => ({ role: m.role, content: m.content }))

      abortControllerRef.current = new AbortController()
      res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, history, sessionId: sessionId.current }),
        signal: abortControllerRef.current.signal,
      })
      abortControllerRef.current = null

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error ?? `Request failed (${res.status})`)
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.answer, sources: data.sources },
      ])

      // Typewriter effect: reveal the answer gradually, then sources one by one
      const fullContent: string = data.answer
      const fullSources: Source[] = data.sources ?? []

      if (streamingRef.current.interval) clearInterval(streamingRef.current.interval)
      sourceTimersRef.current.forEach(clearTimeout)
      sourceTimersRef.current = []
      setStreamingSources(null)

      streamingRef.current.target = fullContent
      streamingRef.current.index = 0
      setStreamingText('')
      streamingRef.current.interval = setInterval(() => {
        streamingRef.current.index = Math.min(streamingRef.current.index + 3, fullContent.length)
        setStreamingText(fullContent.slice(0, streamingRef.current.index))
        scrollToBottom()
        if (streamingRef.current.index >= fullContent.length) {
          clearInterval(streamingRef.current.interval!)
          streamingRef.current.interval = null
          setStreamingText(null)
          // Reveal sources one by one
          if (fullSources.length > 0) {
            setStreamingSources([])
            fullSources.forEach((src, i) => {
              const t = setTimeout(() => {
                setStreamingSources((prev) => (prev ? [...prev, src] : [src]))
                if (i === fullSources.length - 1) {
                  const done = setTimeout(() => setStreamingSources(null), 300)
                  sourceTimersRef.current.push(done)
                }
              }, 400 + i * 350)
              sourceTimersRef.current.push(t)
            })
          }
        }
      }, 20)
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      reportApiError(err, {
        endpoint: '/chat',
        status: res?.status,
        sessionId: sessionId.current,
        statusText: res?.statusText,
        messageLength: trimmed.length,
      })
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.', error: true }])
    } finally {
      setLoading(false)
      scrollToBottom()
      inputRef.current?.focus()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    send(input)
  }

  const empty = messages.length === 0

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: 'primary.main',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Research Assistant
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Query our library with a question you may have.
          </Typography>
        </Box>
      </Box>

      {/* AI status notice */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          px: { xs: 2, md: 3 },
          py: 1.5,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderBottom: '1px solid',
          borderColor: 'rgba(25, 118, 210, 0.18)',
          color: 'text.primary',
        }}
      >
        <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.15, flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
            Please note
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            This is a closed AI tool. Its responses will continue to improve as we update the underlying knowledge base and refine the system.
          </Typography>
        </Box>
      </Box>

      {/* Message thread */}
      <Box
        ref={threadRef}
        sx={{
          minHeight: 240,
          maxHeight: 480,
          overflowY: 'auto',
          px: { xs: 2, md: 3 },
          py: 3,
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {empty && (
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Ask a question or try one of these:
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {SUGGESTED_PROMPTS.map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  variant="outlined"
                  size="small"
                  onClick={() => send(prompt)}
                  sx={{
                    cursor: 'pointer',
                    borderColor: 'rgba(138, 154, 91, 0.32)',
                    backgroundColor: 'rgba(138, 154, 91, 0.2)',
                    color: 'primary.dark',
                    fontSize: '0.78rem',
                    height: 'auto',
                    py: 0.5,
                    '& .MuiChip-label': { whiteSpace: 'normal' },
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(184, 115, 51, 0.24)',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {messages.map((msg, i) => {
          const isLastAssistant = msg.role === 'assistant' && i === messages.length - 1
          const displayContent = isLastAssistant && streamingText !== null ? streamingText : msg.content
          return (
            <Box
              key={i}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: 0.75,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {msg.role === 'assistant' && (
                <AutoAwesomeIcon sx={{ fontSize: 13, color: '#B87333' }} />
              )}
              {msg.role === 'user' && (
                <PersonIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: msg.role === 'assistant' ? 'primary.main' : 'text.disabled',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {msg.role === 'assistant' ? 'Research Assistant' : 'You'}
              </Typography>
            </Box>

            <Box
              sx={{
                maxWidth: { xs: '92%', md: '78%' },
                px: 2,
                py: 1.5,
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                backgroundColor:
                  msg.role === 'user'
                    ? 'primary.main'
                    : msg.error
                    ? 'rgba(211, 47, 47, 0.06)'
                    : 'background.paper',
                border: msg.role === 'assistant' ? '1px solid' : 'none',
                borderColor: msg.error ? 'error.light' : 'divider',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              {msg.role === 'user' ? (
                <Typography
                  variant="body2"
                  sx={{ color: '#fff', whiteSpace: 'pre-wrap', lineHeight: 1.65 }}
                >
                  {displayContent}
                </Typography>
              ) : (
                <Box
                  sx={{
                    color: msg.error ? 'error.main' : 'text.primary',
                    fontSize: '0.875rem',
                    lineHeight: 1.65,
                    '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                    '& ul, & ol': { mt: 0.5, mb: 1, pl: 2.5 },
                    '& li': { mb: 0.25 },
                    '& strong': { fontWeight: 700 },
                    '& a': { color: 'primary.main' },
                  }}
                >
                  <ReactMarkdown>{displayContent}</ReactMarkdown>
                </Box>
              )}
            </Box>

            {(() => {
              const sourcesToShow = isLastAssistant && streamingSources !== null
                ? streamingSources
                : (msg.sources ?? [])
              const hiddenWhileStreaming = isLastAssistant && streamingText !== null
              return msg.role === 'assistant' && !hiddenWhileStreaming && sourcesToShow.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '78%' }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', width: '100%', mb: 0.25 }}>
                    Sources:
                  </Typography>
                  {sourcesToShow.map((src) => (
                    <Chip
                      key={src.id}
                      label={`${src.id} — ${src.title}`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 22,
                        backgroundColor: 'rgba(138, 154, 91, 0.1)',
                        color: 'primary.dark',
                        border: '1px solid rgba(138, 154, 91, 0.2)',
                      }}
                    />
                  ))}
                </Box>
              ) : null
            })()}
          </Box>
          )
        })}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} sx={{ color: 'primary.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Searching the library…
            </Typography>
          </Box>
        )}

      </Box>

      {/* Input */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask a question about regenerative agriculture…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(input)
            }
          }}
          variant="outlined"
          size="small"
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              fontSize: '0.9rem',
            },
          }}
        />
        {isBusy ? (
          <IconButton
            onClick={stop}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              borderRadius: 2,
              width: 40,
              height: 40,
              flexShrink: 0,
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            <StopIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            type="submit"
            disabled={!input.trim()}
            sx={{
              backgroundColor: 'primary.main',
              color: '#fff',
              borderRadius: 2,
              width: 40,
              height: 40,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'primary.dark' },
              '&.Mui-disabled': { backgroundColor: 'action.disabledBackground', color: 'action.disabled' },
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Paper>
  )
}
